import React, { useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import * as THREE from 'three';
import { useNeura } from '../../contexts/NeuraContext.tsx';

const HologramContainer = styled.div`
  width: 100%;
  height: 400px;
  position: relative;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 20px;
  overflow: hidden;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 255, 136, 0.2);
  box-shadow: 0 0 30px rgba(0, 255, 136, 0.1);
`;

const HologramOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, 
    rgba(0, 255, 136, 0.1) 0%,
    rgba(0, 0, 0, 0) 50%,
    rgba(136, 255, 0, 0.1) 100%
  );
  pointer-events: none;
`;

const QuantumStats = styled.div`
  position: absolute;
  top: 1rem;
  left: 1rem;
  color: #00ff88;
  font-family: 'Courier New', monospace;
  font-size: 0.8rem;
  z-index: 2;
  text-shadow: 0 0 10px rgba(0, 255, 136, 0.5);
`;

export const QuantumHologram = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { neuralActivity } = useNeura();
  const statsRef = useRef<{ entangledParticles: number }>({ entangledParticles: 0 });

  useEffect(() => {
    if (!containerRef.current) return;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, containerRef.current.clientWidth / containerRef.current.clientHeight, 0.1, 1000);
    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    
    renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
    containerRef.current.appendChild(renderer.domElement);

    // Create quantum particles
    const particles: THREE.Mesh[] = [];
    const particleGeometry = new THREE.IcosahedronGeometry(0.2, 1);
    const particleMaterial = new THREE.ShaderMaterial({
      uniforms: {
        time: { value: 0.0 },
        color: { value: new THREE.Color(0x00ff88) },
      },
      vertexShader: `
        uniform float time;
        varying vec2 vUv;
        
        void main() {
          vUv = uv;
          vec3 pos = position;
          pos.x += sin(pos.y * 2.0 + time) * 0.1;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 color;
        uniform float time;
        varying vec2 vUv;
        
        void main() {
          float intensity = sin(vUv.x * 10.0 + time) * 0.5 + 0.5;
          gl_FragColor = vec4(color * intensity, 0.7);
        }
      `,
      transparent: true,
    });

    for (let i = 0; i < 20; i++) {
      const particle = new THREE.Mesh(particleGeometry, particleMaterial.clone());
      particle.position.set(
        (Math.random() - 0.5) * 10,
        (Math.random() - 0.5) * 10,
        (Math.random() - 0.5) * 10
      );
      scene.add(particle);
      particles.push(particle);
    }

    // Create quantum entanglement lines
    const entanglementGeometry = new THREE.BufferGeometry();
    const entanglementMaterial = new THREE.LineBasicMaterial({
      color: 0x00ff88,
      transparent: true,
      opacity: 0.3,
    });

    const quantumConnections: THREE.Line[] = [];
    for (let i = 0; i < particles.length; i += 2) {
      const points = [particles[i].position, particles[i + 1].position];
      const geometry = new THREE.BufferGeometry().setFromPoints(points);
      const line = new THREE.Line(geometry, entanglementMaterial);
      scene.add(line);
      quantumConnections.push(line);
    }

    camera.position.z = 15;

    let frame = 0;
    const animate = () => {
      requestAnimationFrame(animate);
      frame += 0.01;

      // Update shader uniforms
      particles.forEach((particle, i) => {
        const material = particle.material as THREE.ShaderMaterial;
        material.uniforms.time.value = frame;
        
        // Quantum fluctuations
        particle.position.x += Math.sin(frame + i) * 0.02;
        particle.position.y += Math.cos(frame + i) * 0.02;
        particle.position.z += Math.sin(frame * 0.5 + i) * 0.02;
        
        particle.rotation.x += 0.01;
        particle.rotation.y += 0.01;
      });

      // Update entanglement lines
      quantumConnections.forEach((line, i) => {
        const positions = line.geometry.attributes.position.array as Float32Array;
        positions[0] = particles[i * 2].position.x;
        positions[1] = particles[i * 2].position.y;
        positions[2] = particles[i * 2].position.z;
        positions[3] = particles[i * 2 + 1].position.x;
        positions[4] = particles[i * 2 + 1].position.y;
        positions[5] = particles[i * 2 + 1].position.z;
        line.geometry.attributes.position.needsUpdate = true;
      });

      // Rotate camera in quantum space
      camera.position.x = Math.cos(frame * 0.5) * 15;
      camera.position.z = Math.sin(frame * 0.5) * 15;
      camera.lookAt(0, 0, 0);

      statsRef.current.entangledParticles = Math.floor(10 + Math.sin(frame) * 5);

      renderer.render(scene, camera);
    };

    animate();

    return () => {
      renderer.dispose();
      containerRef.current?.removeChild(renderer.domElement);
    };
  }, [neuralActivity]);

  return (
    <HologramContainer ref={containerRef}>
      <HologramOverlay />
      <QuantumStats>
        QUANTUM STATUS:
        <br />
        Entangled Particles: {statsRef.current.entangledParticles}
        <br />
        Coherence: 99.99%
        <br />
        Q-Bits Active: 1024
      </QuantumStats>
    </HologramContainer>
  );
}; 